import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Flame, Droplet, Wind, Bell, ShieldCheck, Activity, Gauge, Power, Clock } from "lucide-react";

/**
 * Fire Command Center — sidebar-free 55"-friendly wall display.
 * Auto-cycles across all sites every 15s, shows all key telemetry in a single screen.
 * GR-08
 */
export default function FCCDisplay() {
  const [sites, setSites] = useState([]);
  const [idx, setIdx] = useState(0);
  const [tele, setTele] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data || []));
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  const currentSite = sites[idx % Math.max(sites.length, 1)];

  // Auto-cycle every 15s
  useEffect(() => {
    if (sites.length < 2) return;
    const i = setInterval(() => setIdx((x) => x + 1), 15000);
    return () => clearInterval(i);
  }, [sites.length]);

  // Pull telemetry for current site
  useEffect(() => {
    if (!currentSite) return;
    const load = () => {
      api.get(`/telemetry/${currentSite.site_id}`).then((r) => setTele(r.data)).catch(() => {});
      api.get("/alerts", { params: { status: "open", site_id: currentSite.site_id } }).then((r) => setAlerts(r.data || [])).catch(() => {});
    };
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [currentSite]);

  if (!currentSite) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-gray-500">Loading FCC display…</div>;
  }

  const hydrantOk = (tele.hydrant_pressure_bar || 0) >= 3.5;
  const sprinklerOk = (tele.sprinkler_pressure_bar || 0) >= 5;
  const tankOk = (tele.ug_tank_pct || 0) >= 30 && (tele.oht_tank_pct || 0) >= 25;
  const dieselOk = (tele.diesel_tank_pct || 0) >= 20;
  const allOk = hydrantOk && sprinklerOk && tankOk && dieselOk && alerts.length === 0;

  const Tile = ({ icon: Icon, label, value, sub, tone = "blue", testid }) => {
    const grad = {
      green: "from-emerald-400 to-emerald-600",
      red:   "from-red-500 to-pink-600",
      blue:  "from-sky-400 to-indigo-500",
      amber: "from-amber-400 to-orange-500",
    }[tone];
    return (
      <div className={`rounded-lg p-6 bg-gradient-to-br ${grad} shadow-2xl`} data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
          <Icon className="w-10 h-10 text-gray-900/90" />
          <span className="text-xs uppercase tracking-widest text-gray-900/70 font-bold">{label}</span>
        </div>
        <div className="text-5xl font-extrabold text-gray-900 tracking-tight">{value}</div>
        {sub && <div className="text-sm text-gray-900/80 mt-2">{sub}</div>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white p-8" data-testid="fcc-display">
      {/* HEADER BAR */}
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-lg f360-gradient flex items-center justify-center shadow-xl"><Flame className="w-7 h-7 text-gray-900" strokeWidth={2.5} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold">Fire Command Center · ACMS Live</div>
            <h1 className="text-3xl font-extrabold">{currentSite.name}</h1>
            <div className="text-sm text-gray-500 mt-0.5">{currentSite.address || currentSite.city || "—"} · Site {idx % sites.length + 1} of {sites.length}</div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-xs uppercase tracking-widest font-bold mb-1 ${allOk ? "text-emerald-400" : "text-red-400"}`}>System Status: {allOk ? "ALL CLEAR" : "ATTENTION"}</div>
          <div className="text-4xl font-mono tabular-nums tracking-tight">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
          <div className="text-xs text-gray-500">{now.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        </div>
      </header>

      {/* CRITICAL ALERTS RIBBON */}
      {alerts.length > 0 && (
        <div className="bg-red-500/15 border border-red-500/40 rounded-lg px-6 py-4 mb-6 flex items-center gap-4 animate-pulse" data-testid="fcc-alerts-ribbon">
          <Bell className="w-7 h-7 text-red-300" strokeWidth={2.5} />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-red-300 font-bold">{alerts.length} Open Alert{alerts.length === 1 ? "" : "s"}</div>
            <div className="text-base font-bold text-gray-900">{alerts[0]?.message || alerts[0]?.title || "Critical alert active"}</div>
          </div>
          <div className="text-xs text-red-200">{alerts[0]?.severity?.toUpperCase()}</div>
        </div>
      )}

      {/* PRIMARY KPI GRID — 8 BIG TILES */}
      <div className="grid grid-cols-4 gap-5 mb-6">
        <Tile icon={Gauge} label="Hydrant" value={`${(tele.hydrant_pressure_bar || 0).toFixed(2)}`} sub="bar · GR ≥ 3.5" tone={hydrantOk ? "green" : "red"} testid="tile-hydrant" />
        <Tile icon={Gauge} label="Sprinkler" value={`${(tele.sprinkler_pressure_bar || 0).toFixed(2)}`} sub="bar · GR ≥ 5.0" tone={sprinklerOk ? "green" : "red"} testid="tile-sprinkler" />
        <Tile icon={Droplet} label="UG Tank" value={`${(tele.ug_tank_pct || 0).toFixed(0)}%`} sub={`${(tele.ug_tank_liters || 0).toLocaleString()} L`} tone={(tele.ug_tank_pct || 0) >= 30 ? "blue" : "amber"} testid="tile-ug" />
        <Tile icon={Droplet} label="OHT" value={`${(tele.oht_tank_pct || 0).toFixed(0)}%`} sub={`${(tele.oht_tank_liters || 0).toLocaleString()} L`} tone={(tele.oht_tank_pct || 0) >= 25 ? "blue" : "amber"} testid="tile-oht" />
        <Tile icon={Power} label="Main Pump" value={(tele.main_hydrant_pump || "—").toUpperCase()} sub={tele.power_main_hydrant ? "MAINS" : "OFFLINE"} tone={tele.main_hydrant_pump === "running" ? "green" : "blue"} testid="tile-main-pump" />
        <Tile icon={Power} label="Standby" value={(tele.standby_pump || "—").toUpperCase()} sub="Diesel" tone={tele.standby_pump === "running" ? "amber" : "blue"} testid="tile-standby" />
        <Tile icon={Power} label="Jockey" value={(tele.jockey_pump || "—").toUpperCase()} sub={`${tele.jockey_cycles_per_hr || 0}/hr`} tone={tele.jockey_pump === "running" ? "green" : "blue"} testid="tile-jockey" />
        <Tile icon={Activity} label="Diesel" value={`${(tele.diesel_tank_pct || 0).toFixed(0)}%`} sub="GR ≥ 20%" tone={dieselOk ? "green" : "red"} testid="tile-diesel" />
      </div>

      {/* COMPLIANCE STATUS BAR */}
      <div className="bg-gray-50 rounded-lg p-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Hydrant Pressure", ok: hydrantOk },
            { label: "Sprinkler Pressure", ok: sprinklerOk },
            { label: "Tank Levels", ok: tankOk },
            { label: "Diesel Reserve", ok: dieselOk },
            { label: "Open Alerts", ok: alerts.length === 0 },
          ].map((c) => (
            <div key={c.label} className={`flex items-center gap-2 p-3 rounded-lg ${c.ok ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
              <ShieldCheck className={`w-5 h-5 ${c.ok ? "text-emerald-400" : "text-red-400"}`} strokeWidth={2.5} />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{c.label}</div>
                <div className={`text-sm font-bold ${c.ok ? "text-emerald-300" : "text-red-300"}`}>{c.ok ? "OK" : "ATTENTION"}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <footer className="mt-6 text-center text-xs text-gray-500">
        <Clock className="w-3.5 h-3.5 inline mr-1" strokeWidth={2.5} /> Auto-cycle every 15s · Auto-refresh 5s · Fire360Pro ACMS Platform · GR-08 FCC Display Mode
      </footer>
    </div>
  );
}
